import { h, computed } from 'vue'
import { createColumnHelper } from '@tanstack/vue-table'
import { Checkbox } from '@n11/so-ui-components';
import Label from "@/components/DataTable/Label";
import BasicInput from "@/components/DataTable/BasicInput"
import VariantCell from "@/components/DataTable/VariantCell"
import BasicNativeSelect from "@/components/DataTable/BasicNativeSelect"
import DeleteButton from "@/components/DataTable/DeleteButton"
import TableHeadTooltip from "@/components/DataTable/TableHeadTooltip"
import store from '@/store';
import { CATALOG_TABLE_COLUMNS, CATALOG_TABLE_COLUMN_NAMES, VAT_RATES } from "@/utils/enum/tableColumns"

const minStock = 0;
const maxStock = 99999;

export function columnService() {
  const columnHelper = createColumnHelper();
  const columns = [];

  const formatPrice = (price) => parseFloat(price).toLocaleString("tr-TR", { style: 'currency', currency: 'TRY' });

  const validationMethods = {
    listPrice: (value, row, field) => {
      const numericValue = parseFloat(value);
      if (!numericValue && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = "Piyasa Satış Fiyatı bilgisi zorunludur.";
        return false;
      }

      return true;
    },
    salePrice: (value, row, field) => {
      const val = parseFloat(value);
      const listPrice = row.listPrice

      if (!val && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = "n11 Satış Fiyatı bilgisi zorunludur.";
        return false;
      }

      if (val === 0) {
        row.validations[field].error = true;
        row.validations[field].message = "n11 Satış Fiyatı Piyasa Satış Fiyatı'ndan büyük olamaz.";
        return false;
      }

      if (val > parseFloat(listPrice)) {
        row.validations[field].error = true;
        row.validations[field].message =
          "n11 Satış Fiyatı Piyasa Satış Fiyatı'ndan büyük olamaz.";
        return false;
      }

      return true;
    },
    duration: (value, row, field) => {
      const val = Number(value);

      if ((val === 0 || !val) && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = "Hazırlık süresi 1 günden az olamaz.";
        return false;
      }

      if (isNaN(value)) {
        row.validations[field].error = true;
        row.validations[field].message = "Hazırlık süresi numerik olmalıdır.";
        return false;
      }

      if (val < 0) {
        row.validations[field].error = true;
        row.validations[field].message =
          "Hazırlık süresi sıfırdan büyük olmalıdır.";
        return false;
      }

      return true;
    },
    stockCode: (value, row, field) => {
      if (!value && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = "Satıcı Stok Kodu zorunludur.";
        return false;
      }

      return true;
    },

    stock(value, row, field) {
      const val = Number(value);
      if (!val && row.validations[field].isDirty && value !== '0') {
        row.validations[field].error = true;
        row.validations[field].message = "Stok bilgisi zorunludur.";
        return false;
      }

      if (val < minStock) {
        row.validations[field].error = true;
        row.validations[field].message = "Stok bilgisi sıfır veya sıfırdan büyük olmalıdır.";
        return false;
      }

      if (val > maxStock) {
        row.validations[field].error = true;
        row.validations[field].message = `Stok bilgisi max ${maxStock} olmalıdır.`;
        return false;
      }

      return true;
    },

    vatRate(value, row, field) {
      if (
        (row.validations[field].isDirty && value === undefined) ||
        value === ""
      ) {
        row.validations[field].error = true;
        row.validations[field].message = "KDV değeri girilmelidir.";
        return false;
      }

      if (
        row.validations[field].isDirty &&
        !Object.keys(VAT_RATES).includes(value)
      ) {
        row.validations[field].error = true;
        row.validations[field].message = `KDV değeri %${Object.keys(
          VAT_RATES
        ).join(", %")} olarak girilmelidir.`;
        return false;
      }

      return true;
    },
  };

  if (
    CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.SELECT_COLUMN)
  ) {
    columns.push(
      columnHelper.accessor("select-col", {
        header: generateCheckboxHeader,
        cell: generateCheckboxCell,
        size: 45,
        noVariantsSize: 0,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.VARIANT_INFO)) {
    columns.push(
      columnHelper.accessor('name', {
        header: () => 'Varyant Bilgisi',
        cell: ({ row, getValue }) => {
          const imgUrl = row.original.imgUrl;
          return h(VariantCell, {
            content: getValue() || '',
            imgUrl: imgUrl,
            tooltipTrigger: 'hover',
            tooltipPosition: 'right',
            tooltipWidth: 378,
            showArrow: true,
            offsetTooltipImage: [0, 12],
          });
        },
        size: 164,
        noVariantsSize: 0,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.GTIN)) {
    columns.push(
      columnHelper.accessor('gtin', {
        header: () => 'GTIN',
        cell: ({ getValue }) => h(Label, { content: getValue() || '' }),
        size: 130,
        noVariantsSize: 130,
      })
    );
  }

  const norm = (v) => {
    if (v == null) return "";
    if (typeof v === "number") return String(v);
    const s = String(v).trim();
    return s.replace(/\./g, "").replace(",", ".");
  };

  const validate = (value, field, row, force = false, rows) => {
    const validateFields = row.validations[field];
    const current = norm(value);
    const last = validateFields._lastValue ?? null;

    if (
      validateFields.error &&
      validateFields.message &&
      last === current &&
      !force
    ) {
      return false;
    }

    validateFields.error = false;
    validateFields.message = null;
    const ok = validationMethods[field](value, row, field, rows);


    if (force) validateFields.isDirty = true;

    validateFields._lastValue = current;

    return ok;
  };

  const blur = (value, field, row, table) => {
    const categoryId = store.getters["categories/getSelectedCategoryId"];
    row.validations[field].isDirty = true;
    table.options.meta?.updateRow(row.id, field, value);

    if (field === "duration") {
      apiValidateDuration(row, field, value, table, categoryId);
    }

    if (field === "stockCode") {
      apiValidateStockCode(value, row, field);
    }

    return true;
  };

  const getErrorTooltipText = (validations, accessor) =>
    validations[accessor].error && validations[accessor].message
      ? validations[accessor].message
      : null;

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.STOCK_CODE_WITH_INPUT)) {
    columns.push(
      columnHelper.accessor("stockCode", {
        header: () => "Stok Kodu",
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const rows = table.options.meta?.getColumnDataExceptThis(
            accessorKey,
            row.id
          );
          const original = row.original;
          return h(BasicInput, {
            value: original[accessorKey],
            isValid: !original.validations[accessorKey].error && validate(original[accessorKey], accessorKey, original, false, rows),
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey
            ),
          });
        },
        size: 106,
        noVariantsSize: 180,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.LIST_PRICE)) {
    columns.push(
      columnHelper.accessor('listPrice', {
        header: () => h(TableHeadTooltip,{
          title: 'Piyasa<br /> Satış Fiyatı',
          tooltipTrigger: 'click',
          tooltipText: "Piyasa Satış Fiyatı, ürünün indirim uygulanmadan önce son 30 gündeki minimum satış fiyatı olmalıdır. Bu alana girdiğiniz fiyat, n11’deki ürün sayfasına yansımayacaktır.",
          }),
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef
          const original = row.original
          return h(BasicInput, {
            value: original[accessorKey],
            isValid: validate(original[accessorKey], accessorKey, original),
            inputId: `${accessorKey}-${original.id}`,
            type: 'currency',
            isPrice: true,
            disableAutoFocus: true,
            blur: (value) => blur(value, accessorKey, original, table),
            format: formatPrice,
            currencyDisplay:'symbol',
            currencyCode: "TRY",
            errorTooltipText: getErrorTooltipText(original.validations, accessorKey),
          });
        },
        size: 130,
        noVariantsSize: 170,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.SALES_PRICE)) {
    columns.push(
      columnHelper.accessor('salePrice', {
        header: () => h('span',{
            innerHTML: "n11 <br /> Satış Fiyatı",
          class: "prices"
          }),
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef 
          const original = row.original
          return h(BasicInput, {
            value: original[accessorKey],
            inputId: `${accessorKey}-${original.id}`,
            isValid: validate(original[accessorKey], accessorKey, original),
            isPrice: true,
            disableAutoFocus: true,
            type: 'currency',
            blur: (value) => blur(value, accessorKey, original, table),
            format: formatPrice,
            currencyDisplay:'symbol',
            currencyCode: "TRY",
            errorTooltipText: getErrorTooltipText(original.validations, accessorKey),
          });
        },
        size: 130,
        noVariantsSize: 170,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.STOCK)) {
    columns.push(
      columnHelper.accessor('stock', {
        header: () => 'Stok (Adet)',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef 
          const original = row.original

          return h(BasicInput, {
            value: original[accessorKey],
            isValid: validate(original[accessorKey], accessorKey, original),
            min: minStock,
            max: maxStock,
            type: 'number',
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(original.validations, accessorKey),
          });
        },
        size: 105,
        noVariantsSize: 176,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.DURATION)) {
    columns.push(
      columnHelper.accessor("duration", {
        header: () => "Hazırlık Süresi (Gün)",
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          return h(BasicInput, {
            value: original[accessorKey],
            type: "number",
            isValid: !original.validations[accessorKey].error && validate(original[accessorKey], accessorKey, original),
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: computed(() =>
              getErrorTooltipText(original.validations, accessorKey)
            ),
          });
        },
        size: 106,
        noVariantsSize: 176,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.VAT_RATES)) {
    columns.push(
      columnHelper.accessor('vatRate', {
        header: 'KDV Oranı',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          return h(BasicNativeSelect, {
            onChange: (e) => blur(e.target.value, accessorKey, original, table),
            isValid: validate(original[accessorKey], accessorKey, original, false),
            selectValue: row.original.vatRate,
            pid: `id-${row.original.id}`,
            options: VAT_RATES,
            errorTooltipText: getErrorTooltipText(original.validations, accessorKey),
          });
        },
        size: 131,
        noVariantsSize: 0,
      })
    );
  }

  if (CATALOG_TABLE_COLUMN_NAMES.includes(CATALOG_TABLE_COLUMNS.ACTIONS)) {
    columns.push(
      columnHelper.accessor('actions', {
        header: 'İşlemler',
        cell: ({ row }) => h(DeleteButton, {
            pid: row.original.id,
          }),
        size: 80,
        noVariantsSize: 0,
      })
    );
  }

  function generateCheckboxHeader(props) {
    const { table } = props;

    return h(Checkbox, {
      id: 'products-table-selectAll',
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler(),
    }, { default: () => [] });
  }

  function generateCheckboxCell(props) {
    const { row } = props;

    return h(Checkbox, {
        id: `product-${row.original.id}`,
        value: row.original.id,
        productId: `product-${row.idproductId}`,
        indeterminate: row.getIsSomeSelected(),
        checked: row.getIsSelected(),
        checkBoxDisabled: !row.getCanSelect(),
        onChange: row.getToggleSelectedHandler(),
      },
      { default: () => [] }
    );
  }

  async function apiValidateDuration(row, field, value, table, categoryId) {
    try {
      const response = await store.dispatch("validations/validatePreparingTime", {
        day: Number(value),
        id: categoryId,
      });
      if (response?.errorCode) {
        row.validations[field].error = true;
        row.validations[field].message = response.message;
      } else {
        row.validations[field].error = false;
        row.validations[field].message = null;
      }
    } catch (e) {
      const errorMessage = e?.response?.data?.message || e?.data?.message;

      row.validations[field].error = true;
      row.validations[field].message = errorMessage;
    }
  }

  async function apiValidateStockCode(value, row, field) {
    try {
      const response = await store.dispatch("validations/validateStockCode", value);

      if (response.exists) {
        row.validations[field] = {
          ...row.validations[field],
          error: true,
          message: response.message,
        };
      } else {
        row.validations[field] = {
          ...row.validations[field],
          error: false,
          message: null,
        };
      }
    } catch (e) {
      const errorMessage =
        e?.response?.data?.message ||
        e?.data?.message ||
        "Bilinmeyen bir hata oluştu.";

      row.validations[field].error = true;
      row.validations[field].message = errorMessage;
    }
  }

  return {
    columns,
    validate,
  };
}
